import React from 'react';
import { View as PDFView } from '@react-pdf/renderer';

// styled-system v5 default space scale
const SPACE = [0, 4, 8, 16, 32, 64, 128, 256, 512];
const sp = (v: any): any =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < SPACE.length
    ? SPACE[v]
    : v;

interface ViewProps {
  style?: any;
  children?: React.ReactNode;
  m?: any;
  mt?: any;
  mb?: any;
  ml?: any;
  mr?: any;
  mx?: any;
  my?: any;
  p?: any;
  pt?: any;
  pb?: any;
  pl?: any;
  pr?: any;
  px?: any;
  py?: any;
  width?: any;
  height?: any;
  minWidth?: any;
  maxWidth?: any;
  minHeight?: any;
  maxHeight?: any;
  flex?: any;
  flexGrow?: any;
  flexShrink?: any;
  flexBasis?: any;
  alignSelf?: any;
  flexDirection?: any;
  flexWrap?: any;
  justifyContent?: any;
  alignItems?: any;
  justifySelf?: any;
  bg?: any;
  backgroundColor?: any;
  border?: any;
  borderTop?: any;
  borderBottom?: any;
  borderLeft?: any;
  borderRight?: any;
  borderColor?: any;
  borderRadius?: any;
  borderWidth?: any;
  position?: any;
  top?: any;
  bottom?: any;
  left?: any;
  right?: any;
  zIndex?: any;
  fontSize?: any;
  fontWeight?: any;
  textAlign?: any;
  color?: any;
  [key: string]: any;
}

const View = ({
  style,
  children,
  // space
  m,
  mt,
  mb,
  ml,
  mr,
  mx,
  my,
  p,
  pt,
  pb,
  pl,
  pr,
  px,
  py,
  // layout
  width,
  height,
  minWidth,
  maxWidth,
  minHeight,
  maxHeight,
  flex,
  flexGrow,
  flexShrink,
  flexBasis,
  alignSelf,
  // flexbox
  flexDirection,
  flexWrap,
  justifyContent,
  alignItems,
  justifySelf,
  // background
  bg,
  backgroundColor,
  // border
  border,
  borderTop,
  borderBottom,
  borderLeft,
  borderRight,
  borderColor,
  borderRadius,
  borderWidth,
  // position
  position,
  top,
  bottom,
  left,
  right,
  zIndex,
  // typography
  fontSize,
  fontWeight,
  textAlign,
  // color
  color,
  // react-pdf extras
  ...rest
}: ViewProps) => {
  const computed: Record<string, any> = {};

  // space
  if (m !== undefined) computed.margin = sp(m);
  if (mt !== undefined) computed.marginTop = sp(mt);
  if (mb !== undefined) computed.marginBottom = sp(mb);
  if (ml !== undefined) computed.marginLeft = sp(ml);
  if (mr !== undefined) computed.marginRight = sp(mr);
  if (mx !== undefined) {
    computed.marginLeft = sp(mx);
    computed.marginRight = sp(mx);
  }
  if (my !== undefined) {
    computed.marginTop = sp(my);
    computed.marginBottom = sp(my);
  }
  if (p !== undefined) computed.padding = sp(p);
  if (pt !== undefined) computed.paddingTop = sp(pt);
  if (pb !== undefined) computed.paddingBottom = sp(pb);
  if (pl !== undefined) computed.paddingLeft = sp(pl);
  if (pr !== undefined) computed.paddingRight = sp(pr);
  if (px !== undefined) {
    computed.paddingLeft = sp(px);
    computed.paddingRight = sp(px);
  }
  if (py !== undefined) {
    computed.paddingTop = sp(py);
    computed.paddingBottom = sp(py);
  }

  // layout
  if (width !== undefined) computed.width = width;
  if (height !== undefined) computed.height = height;
  if (minWidth !== undefined) computed.minWidth = minWidth;
  if (maxWidth !== undefined) computed.maxWidth = maxWidth;
  if (minHeight !== undefined) computed.minHeight = minHeight;
  if (maxHeight !== undefined) computed.maxHeight = maxHeight;
  if (flex !== undefined) computed.flex = flex;
  if (flexGrow !== undefined) computed.flexGrow = flexGrow;
  if (flexShrink !== undefined) computed.flexShrink = flexShrink;
  if (flexBasis !== undefined) computed.flexBasis = flexBasis;
  if (alignSelf !== undefined) computed.alignSelf = alignSelf;

  // flexbox
  if (flexDirection !== undefined) computed.flexDirection = flexDirection;
  if (flexWrap !== undefined) computed.flexWrap = flexWrap;
  if (justifyContent !== undefined) computed.justifyContent = justifyContent;
  if (alignItems !== undefined) computed.alignItems = alignItems;
  if (justifySelf !== undefined) computed.justifySelf = justifySelf;

  // background
  if (bg !== undefined) computed.backgroundColor = bg;
  if (backgroundColor !== undefined) computed.backgroundColor = backgroundColor;

  // border
  if (border !== undefined) computed.border = border;
  if (borderTop !== undefined) computed.borderTop = borderTop;
  if (borderBottom !== undefined) computed.borderBottom = borderBottom;
  if (borderLeft !== undefined) computed.borderLeft = borderLeft;
  if (borderRight !== undefined) computed.borderRight = borderRight;
  if (borderColor !== undefined) computed.borderColor = borderColor;
  if (borderRadius !== undefined) computed.borderRadius = borderRadius;
  if (borderWidth !== undefined) computed.borderWidth = borderWidth;

  // position
  if (position !== undefined) computed.position = position;
  if (top !== undefined) computed.top = top;
  if (bottom !== undefined) computed.bottom = bottom;
  if (left !== undefined) computed.left = left;
  if (right !== undefined) computed.right = right;
  if (zIndex !== undefined) computed.zIndex = zIndex;

  // typography
  if (fontSize !== undefined) computed.fontSize = fontSize;
  if (fontWeight !== undefined) computed.fontWeight = fontWeight;
  if (textAlign !== undefined) computed.textAlign = textAlign;

  // color
  if (color !== undefined) computed.color = color;

  return (
    <PDFView style={[computed, style]} {...rest}>
      {children}
    </PDFView>
  );
};

export default View;
